/**
 * Start and follow services until they really run. Start / Start all used to return as soon as the runner said
 * "started" (its requests only queue the work), so the buttons stopped spinning long before anything answered. Here a
 * start asks the runner to start each service that does not run, then polls each one's status
 * (`GET /api/preview/:sessionId/status`) until every one runs and is ready, fails, or the time runs out.
 *
 * Progress: `{ services: [{ serviceName, sessionId, phase, reason, percent }], error, done }`, `phase` one of PHASES.
 */
import {
  crashedWhileStarting, getRunnables, getServiceStatus, isRunning, restartService, sessionIdFor, startService,
} from '../../services/SandboxService';
import { runnableServiceName } from '../../utils/runnablesModel';
import { cliServiceOf } from '../../utils/cliServices';

export const START_POLL_MS = 2000;
export const START_TIMEOUT_MS = 5 * 60 * 1000;
/**
 * A start the runner shows no sign of after this long, while its status still says it failed: that failure is this
 * start's (the runner refused it or failed it at once, before any change the status shows), not "Waiting" for ever.
 */
export const START_STALE_MS = 15 * 1000;
/** A start whose service the runner still does not know after this long: it never got there. */
export const START_LOST_MS = 30 * 1000;

/** Why a start is refused before it is sent: what the project lacks (`fix` says how to fix it in Apps). */
export const FIX_MISSING_FILES = 'missing-files';

/**
 * What vfs-service already knows stops a start, by the runner's name of each pinned file (a compose file that
 * bind-mounts a file the project lacks): `{ [serviceName]: { problems, missing } }`. Empty when it cannot tell.
 */
export async function knownPreconditions(projectId) {
  let scan = null;
  try {
    scan = typeof getRunnables === 'function' ? await getRunnables(projectId) : null;
  } catch {
    scan = null;
  }
  const out = {};
  Object.entries(scan?.services || {}).forEach(([service, found]) => {
    (found?.runnables || []).forEach((item) => {
      const problems = Array.isArray(item.problems) ? item.problems.filter(Boolean) : [];
      if (!problems.length || !item.kind || !item.file) { return; }
      out[runnableServiceName(service, item)] = { problems, missing: Array.isArray(item.missing) ? item.missing : [] };
    });
  });
  return out;
}

/** One line for a refused start: the first problem, and how many more. */
export const preconditionReason = (problems) => (problems.length > 1 ? `${problems[0]} (and ${problems.length - 1} more)` : problems[0]);

/**
 * Where a service is on its way up, in order; `failed` and `limit` end it without running. `built`: a command-line
 * tool (utils/cliServices.js), over at once: never waited for, not counted.
 */
export const PHASES = ['queued', 'preparing', 'installing', 'building', 'starting', 'running', 'failed', 'limit', 'stopped', 'built'];

export const PHASE_LABELS = {
  queued: 'Waiting',
  preparing: 'Getting the code',
  installing: 'Installing dependencies',
  building: 'Building',
  starting: 'Starting',
  running: 'Running',
  failed: 'Failed',
  limit: 'Not started',
  stopped: 'Stopped',
  built: 'Command-line tool',
};

/** The same, short enough for a button. */
export const PHASE_SHORT_LABELS = {
  queued: 'Starting',
  preparing: 'Preparing',
  installing: 'Installing',
  building: 'Building',
  starting: 'Starting',
  running: 'Running',
  failed: 'Failed',
  limit: 'Not started',
  stopped: 'Stopped',
  built: 'Built',
};

// The runner's `buildPhase` values, by the step they are part of.
const RUNNER_PHASES = {
  // Waiting for another service's install to finish (the runner shares a few install slots).
  queued: 'queued',
  preparing: 'preparing',
  initializing: 'preparing',
  writing: 'preparing',
  installing: 'installing',
  configuring: 'starting',
  starting: 'starting',
  restarting: 'starting',
};

// A crash while the runner still says "building" (its process ended before it was ready, with what it printed last)
// ends the start too: the progress stops at once instead of "Starting 92%…" until the time runs out.
const isFailure = (info) => Boolean(info) && (
  (info.buildStatus === 'failed' && (!info.buildPhase || info.buildPhase === 'error'))
  // "error": a start refused before it ran (a compose file mounting a file the project lacks): over at once.
  || info.buildStatus === 'error'
  || (info.crashed === true && info.buildStatus !== 'building')
  || crashedWhileStarting(info)
);

/** True when the runner reports the service up: its process runs and its build is done. */
export const isReady = (info) => Boolean(info) && info.containerStatus === 'running' && !info.crashed
  && !['building', 'failed', 'error'].includes(info.buildStatus);

/** A first line longer than this is cut (a pasted stack or a minified dump, not a message). */
export const MAX_REASON_CHARS = 600;

/**
 * Why a service failed, in one line: the error's first line, whole. The runner's messages put what to do at the end
 * (compose without Docker: "…its plan needs \"Docker in the sandbox\""), so a short cap hid the actionable part; only
 * a first line past MAX_REASON_CHARS is cut.
 */
export function failureReason(info) {
  const crash = Array.isArray(info?.crashOutput) ? info.crashOutput.map((line) => String(line || '').trim()).filter(Boolean) : [];
  const text = String(info?.buildError || info?.processError || crash[crash.length - 1] || '').trim();
  if (!text) { return info?.crashed ? 'It stopped right after starting: see Logs' : 'See Logs'; }
  const first = (text.split('\n').find((line) => line.trim()) || text).trim();
  return first.length > MAX_REASON_CHARS ? `${first.slice(0, MAX_REASON_CHARS - 1)}…` : first;
}

/**
 * One service's phase from the runner's status `info`. `fresh`: the runner has picked up this start (until then the
 * status still says how the service was before it, e.g. the failure Start is meant to fix, which is not this start's).
 */
export function phaseOf(info, { fresh = true } = {}) {
  if (!info) { return 'queued'; }
  if (isReady(info)) { return 'running'; }
  if (!fresh) { return 'queued'; }
  // Stop reached it while it built or waited for an install slot (the runner's phase `cancelled`): not a failure.
  if (info.buildPhase === 'cancelled') { return 'stopped'; }
  if (isFailure(info)) { return 'failed'; }
  // A failed build being pulled again still says "failed" until its new build begins: its step says it moves.
  const moving = info.buildStatus === 'building' || info.containerStatus === 'running'
    || (Boolean(info.buildPhase) && info.buildPhase !== 'error' && info.buildPhase !== 'ready');
  if (!moving) { return 'queued'; }
  return RUNNER_PHASES[info.buildPhase] || (info.buildStatus === 'building' ? 'building' : 'starting');
}

const ENDED = new Set(['running', 'failed', 'limit', 'stopped', 'built']);

/** `{ total, running, failed, limited, stopped, active, done }` of a progress (command-line tools, `built`, left out). */
export function progressSummary(progress) {
  const services = (progress?.services || []).filter((service) => service.phase !== 'built');
  const count = (phase) => services.filter((service) => service.phase === phase).length;
  const running = count('running');
  const failed = count('failed');
  const limited = count('limit');
  const stopped = count('stopped');
  const done = services.every((service) => ENDED.has(service.phase));
  return { total: services.length, running, failed, limited, stopped, active: !done, done };
}

/**
 * The button's words: "Starting 1/3…" while it goes on; "2/3 running · 1 failed" when something did not start; null
 * when everything runs.
 */
export function progressLabel(progress) {
  const { total, running, failed, limited, stopped, done } = progressSummary(progress);
  if (!total) { return null; }
  if (!done) { return `Starting ${running}/${total}…`; }
  if (running === total) { return null; }
  // Everything that did not run was stopped by the person: nothing to report.
  if (!failed && !limited) { return null; }
  const parts = [`${running}/${total} running`];
  if (failed) { parts.push(`${failed} failed`); }
  if (limited) { parts.push(`${limited} not started`); }
  if (stopped) { parts.push(`${stopped} stopped`); }
  return parts.join(' · ');
}

/** True once a start is over and every one of its services runs (the button goes back to its usual self). */
export const allRunning = (progress) => {
  const { total, running, done } = progressSummary(progress);
  return Boolean(progress) && done && (total > 0 || (progress.services || []).length > 0) && running === total;
};

/** 0–100: the share of services running, with a little credit for those on their way. */
export function progressPercent(progress) {
  const services = (progress?.services || []).filter((service) => service.phase !== 'built');
  if (!services.length) { return 0; }
  const weight = { queued: 0, preparing: 0.15, installing: 0.35, building: 0.55, starting: 0.8, running: 1, failed: 1, limit: 1, stopped: 1 };
  const sum = services.reduce((total, service) => total + (weight[service.phase] ?? 0), 0);
  return Math.round((sum / services.length) * 100);
}

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Starts a service the runner listed: `restartService`, or `startService` when the runner answers 404 (its session
 * was reaped, or the runner restarted, since the list was read): a build from vfs-service, as for one that never ran.
 */
async function restartOrStart(projectId, entry) {
  try {
    return await restartService(entry.sessionId);
  } catch (error) {
    if (error?.status !== 404) { throw error; }
    entry.before = null;
    entry.fresh = true;
    return startFresh(projectId, entry);
  }
}

/** A build from vfs-service: the service, or a file pinned in it (`runnable`, started under the service's folder). */
function startFresh(projectId, entry) {
  return entry.runnable
    ? startService(projectId, entry.runnable.service, { runnable: entry.runnable })
    : startService(projectId, entry.serviceName);
}

/**
 * Starts the services of `items` (`[{ serviceName, service, runnable? }]`, `service` the runner's entry or null; `runnable`
 * a file pinned in a service, utils/runnablesModel, whose `serviceName` is the runner's name for it) that do not run,
 * and follows every one of them (those already building too) until all run and are ready, or fail. The start requests
 * go one after the other (they return at once; the runner builds in parallel), so a plan's running-services limit
 * stops at the first refusal: that one and the rest are `limit`, with the plan's message as `error`.
 * `onProgress(progress)` after every change; `cancelled()` true stops following (the progress so far is returned);
 * `shouldStop()` true (the person pressed Stop meanwhile) starts no more of them: they end as `stopped`, the ones
 * already going end when the runner says it cancelled them.
 * Resolves to the last progress (`done` true unless cancelled).
 */
export async function startServices(projectId, items, {
  onProgress, cancelled = () => false, shouldStop = () => false, pollMs = START_POLL_MS, timeoutMs = START_TIMEOUT_MS,
  staleMs = START_STALE_MS, lostMs = START_LOST_MS, preconditions = knownPreconditions, now = () => Date.now(),
  cliOf = (serviceName, service) => cliServiceOf(projectId, serviceName) || service?.cli || null,
} = {}) {
  const tracked = items.map(({ serviceName, service, runnable = null }) => {
    // Only an entry the runner listed has a session to restart: a placeholder for a folder that never ran
    // (`{ serviceName, buildStatus: 'idle' }`, the Apps list's) has no `sessionId`, and its restart answered 404.
    const known = service?.sessionId ? service : null;
    // A command-line tool (utils/cliServices.js): `built` at once, never followed. One set up as one is built in
    // passing (below); one that is not would start, exit and be started again: never started here.
    const cli = runnable ? null : cliOf(serviceName, service);
    return {
      serviceName,
      runnable,
      sessionId: service?.sessionId || sessionIdFor(projectId, serviceName),
      before: known,
      // Running and done: nothing to do. Building: followed, not started again.
      phase: cli ? 'built' : (isRunning(service) && service.buildStatus !== 'building' ? 'running' : 'queued'),
      toStart: !cli && !isRunning(service),
      cliBuild: Boolean(cli?.configured) && !isRunning(service),
      // Whether the status already speaks of this start: yes with no session yet (the runner answers 404 until it has
      // one) or one already building; else once it changed since the click (an entry without `lastActivityAt`, from an
      // older runner, cannot tell: taken as it comes).
      fresh: !known || known.buildStatus === 'building' || !known.lastActivityAt,
      reason: null,
      percent: null,
      fix: null,
      requestedAt: null,
    };
  });
  let error = null;
  const snapshot = (done = false) => ({
    services: tracked.map(({ serviceName, sessionId, phase, reason, percent, fix }) => ({ serviceName, sessionId, phase, reason, percent, ...(fix ? { fix } : {}) })),
    error,
    done,
  });
  const report = (done) => {
    const progress = snapshot(done);
    onProgress?.(progress);
    return progress;
  };
  report(false);

  // What is already known to stop a start (a compose file mounting a file the project lacks) ends it here: no build is
  // launched, the reason is said at once, and Apps can fix it ("Provide the missing files").
  if (tracked.some((item) => item.toStart)) {
    const known = await Promise.resolve(preconditions ? preconditions(projectId) : {}).catch(() => ({}));
    tracked.filter((item) => item.toStart && known?.[item.serviceName]?.problems?.length).forEach((entry) => {
      entry.toStart = false;
      entry.phase = 'failed';
      entry.reason = preconditionReason(known[entry.serviceName].problems);
      entry.fix = FIX_MISSING_FILES;
    });
  }

  for (const entry of tracked.filter((item) => item.toStart)) {
    if (cancelled()) { return snapshot(false); }
    if (shouldStop()) {
      entry.phase = 'stopped';
      continue;
    }
    if (error && /limit|plan/i.test(error)) {
      entry.phase = 'limit';
      entry.reason = error;
      continue;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      await (entry.before ? restartOrStart(projectId, entry) : startFresh(projectId, entry));
      entry.requestedAt = now();
    } catch (err) {
      if (err?.code === 'limit_running_services') {
        error = err.message;
        entry.phase = 'limit';
        entry.reason = err.message;
      } else {
        entry.phase = 'failed';
        entry.reason = err?.message || 'The start request failed';
        error = error || `Could not start ${entry.serviceName}: ${entry.reason}`;
      }
    }
    report(false);
  }
  // The command-line tools set up as one and not built: their build asked for, nobody waits for it (nor its errors).
  for (const entry of tracked.filter((item) => item.cliBuild && !shouldStop())) {
    // eslint-disable-next-line no-await-in-loop
    await (entry.before ? restartOrStart(projectId, entry) : startFresh(projectId, entry)).catch(() => null);
  }

  const started = Date.now();
  while (tracked.some((entry) => !ENDED.has(entry.phase))) {
    if (cancelled()) { return snapshot(false); }
    if (Date.now() - started >= timeoutMs) {
      tracked.filter((entry) => !ENDED.has(entry.phase)).forEach((entry) => {
        entry.phase = 'failed';
        entry.reason = 'It did not start in time: see Logs';
      });
      break;
    }
    // eslint-disable-next-line no-await-in-loop
    await wait(pollMs);
    if (cancelled()) { return snapshot(false); }
    const pending = tracked.filter((entry) => !ENDED.has(entry.phase));
    // eslint-disable-next-line no-await-in-loop
    const answers = await Promise.all(pending.map((entry) => getServiceStatus(entry.sessionId).then((info) => ({ info }), () => null)));
    pending.forEach((entry, index) => {
      const answer = answers[index];
      if (!answer) { return; } // a blip: ask again
      const { info } = answer;
      const before = entry.before;
      const waited = entry.requestedAt === null ? 0 : now() - entry.requestedAt;
      // The runner still does not know it, long after the request: it never got there.
      if (!info && waited >= lostMs) {
        entry.phase = 'failed';
        entry.reason = 'The runner did not pick up this start: see Logs, or try again';
        return;
      }
      // Still the failure it had before, long after the request: the runner refused or failed it at once.
      if (!entry.fresh && info && waited >= staleMs && isFailure(info)) { entry.fresh = true; }
      // Picked up once the runner shows any change since the click, or once the service is on its way.
      if (!entry.fresh && info && (
        !before || info.lastActivityAt !== before.lastActivityAt || info.restartCount !== before.restartCount
        || info.buildStatus === 'building' || (info.containerStatus === 'running' && before.containerStatus !== 'running')
      )) {
        entry.fresh = true;
      }
      entry.phase = phaseOf(info, { fresh: entry.fresh });
      entry.reason = entry.phase === 'failed' ? failureReason(info) : null;
      entry.percent = typeof info?.buildProgress === 'number' && entry.phase !== 'running' ? Math.round(info.buildProgress) : null;
    });
    report(false);
  }
  return report(true);
}
